// =============================================================================
// bookingStatus.ts — pure status helpers for Bookings V2
//
// Extracted from the cell so the slot rules become unit-testable and the cell
// stays presentational. Aligned to the current `develop` model (Slot / Booking).
//
// develop data flow recap: the lean /bookings response (FacilityBookingsDay) is
// converted by adaptBookingsToSchedules.ts into the per-interval Slot[] / Booking[]
// the grid consumes, stashing:
//     metadata.BookingTitle = summary.title
//     metadata.Price        = String(summary.price)   // and the facility default
//     metadata.BookingNotes = summary.notes
//     metadata.NonMembers   = String(summary.nonMembers)
// Cancelled bookings are filtered out upstream, so cells never see them.
// =============================================================================

import moment from 'moment-timezone';
import { Slot } from '../../../model/bookings/Slot';
import { Booking } from '../../../model/bookings/Booking';
import { isZeroValueAdminBooking } from '../bookingPaymentStatus';
import { PaymentTag, SlotKind } from './bookingTokens';

// ----- Settled ---------------------------------------------------------------

/**
 * A booking is "settled" when there is nothing left to pay: either the payment
 * transaction is Completed, or it's a zero-value admin booking (the club placed
 * it and the transaction amount is £0 / absent). Settled bookings render green
 * (booked) and never pending/overdue.
 */
function isSettled(slot: Slot, booking: Booking, currentClientId?: string): boolean {
  return (
    booking.paymentTransactionStatus === 'Completed' ||
    isZeroValueAdminBooking(slot, booking, currentClientId)
  );
}

// ----- Payment ---------------------------------------------------------------

/**
 * Map a booking to its three-state UI tag, or null when no tag should show.
 *
 * A zero-value admin booking has nothing to pay, so it carries NO tag (pass
 * `slot` + `currentClientId` to enable that suppression). Without a slot the
 * helper degrades to the plain payment-status mapping.
 */
export function derivePaymentTag(
  booking: Booking,
  slot?: Slot,
  currentClientId?: string
): PaymentTag | null {
  if (slot && isZeroValueAdminBooking(slot, booking, currentClientId)) return null;
  if (booking.paymentTransactionStatus === 'Completed') return 'paid';
  if (booking.paymentTransactionStatus === 'Unpaid') return 'unpaid';
  return 'pending'; // any other incomplete state
}

// ----- Advance-booking window --------------------------------------------------

/**
 * Whether the current user may book beyond the member advance-booking window.
 *
 * The window only restricts *regular* members. Anyone who isn't a regular member
 * bypasses it and sees far-ahead empty slots as Available (bookable):
 *   - staff — any admin/front-of-house permission drives `isAdmin`; and
 *   - any non-`Member` member type (e.g. `Coach`).
 *
 * The `memberType !== 'Member'` breadth is deliberate: the requirement is
 * "anyone who isn't a regular member". `memberType` is a server-driven list
 * (`/members/types`), so any future non-Member type inherits the bypass — narrow
 * to an explicit set here if that ever stops being the intent.
 */
export function canMemberBypassAdvanceWindow(isAdmin: boolean, memberType: string): boolean {
  return isAdmin || memberType !== 'Member';
}

// ----- Visual kind (replaces getBookingColor's hex switch) -------------------

export function deriveSlotKind(
  slot: Slot,
  booking: Booking | null,
  canBypassAdvanceWindow = false,
  currentClientId?: string
): SlotKind {
  const isPast = moment().tz(slot.timezone).isAfter(moment(slot.endTime).tz(slot.timezone));
  // The advance-window 'Unavailable' is regular-member-facing only: anyone who
  // can bypass it (staff, or a non-Member member type such as Coach) sees those
  // empty future slots as bookable. A genuine block always arrives with a
  // non-null booking, so the `!booking` guard keeps real blocks greyed for all.
  const isUnavailable = slot.bookingStatus === 'Unavailable' && !(canBypassAdvanceWindow && !booking);

  if (isUnavailable) return 'blocked';

  const settled = booking != null && isSettled(slot, booking, currentClientId);

  // Overdue is only for a real unpaid debt: a past booking that is NOT settled.
  // A zero-value admin booking is settled, so it never lands here — it stays
  // green even in the past.
  if (isPast && booking?.bookingStatus === 'Booked' && !settled) {
    return 'overdue';
  }
  // Only EMPTY past slots are 'past' (striped grey). A past booking still carries
  // its own status — a settled one stays 'booked' (green), so it isn't swallowed
  // by the past catch-all before the settled check below.
  if (isPast && !booking) return 'past';
  if (!booking) return 'available';
  if (settled) return 'booked';
  return 'pending';
}

/** Card-style kinds carry member/price content; the rest render a single label. */
export function isBookingCardKind(kind: SlotKind): boolean {
  return kind === 'booked' || kind === 'pending' || kind === 'overdue';
}

// ----- Title (same precedence as the original getBookingTitle) ---------------

export function deriveSlotTitle(
  slot: Slot,
  booking: Booking | null,
  canBypassAdvanceWindow = false
): string {
  if (slot.metadata?.BookingTitle && booking?.bookingStatus === 'Booked') {
    return slot.metadata.BookingTitle as string;
  }
  if (booking) return booking.memberName;
  // Match deriveSlotKind: an empty advance-window slot reads 'Unavailable' only
  // for regular members. Bypass users see it as an available (bookable) slot, so
  // the label must say 'Available', not contradict the available styling.
  if (slot.bookingStatus === 'Unavailable' && !(canBypassAdvanceWindow && !booking)) {
    return 'Unavailable';
  }
  const isPast = moment().tz(slot.timezone).isAfter(moment(slot.endTime).tz(slot.timezone));
  return isPast ? 'Unavailable' : 'Available';
}

// ----- Price + party (real data, threaded through metadata) ------------------

/** Price in major units, or null if the facility/booking has none. */
export function derivePrice(slot: Slot, booking: Booking | null): number | null {
  const raw = (slot.metadata?.Price ?? booking?.metadata?.Price) as string | number | undefined;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Total party = number of playingMembers (the array already includes the lead member). */
export function derivePartySize(booking: Booking | null): number | null {
  if (!booking) return null;
  return booking.playingMembers?.length ?? 0;
}

// ----- Span ------------------------------------------------------------------

export function getBookingSpan(booking: Booking | null, bookingIntervalMins: number): number {
  if (!booking) return 1;
  const ms = booking.bookingEndTimeUtc.getTime() - booking.bookingDateTimeUtc.getTime();
  return Math.ceil(ms / (1000 * 60) / bookingIntervalMins);
}

// ----- Permissions (pulled out of the click handler) -------------------------

export interface SlotPermissionInput {
  isAdmin: boolean;
  // True for anyone who may book past the member advance-booking window — staff
  // (admin/front-of-house) or a non-Member member type such as Coach. Compute via
  // canMemberBypassAdvanceWindow. Defaults to false (regular-member behaviour).
  canBypassAdvanceWindow?: boolean;
  currentMembershipId: string | undefined;
  slot: Slot;
  booking: Booking | null;
}

export interface SlotPermissions {
  isPast: boolean;
  isUnavailable: boolean;
  isOwnBooking: boolean;
  isDisabled: boolean;
  canClick: boolean;
}

export function deriveSlotPermissions({
  isAdmin,
  canBypassAdvanceWindow = false,
  currentMembershipId,
  slot,
  booking,
}: SlotPermissionInput): SlotPermissions {
  const isPast = moment().tz(slot.timezone).isAfter(moment(slot.endTime).tz(slot.timezone));
  // Advance-window 'Unavailable' is regular-member-facing only; anyone who isn't a
  // regular member — staff (admin/front-of-house) or a non-Member member type such
  // as Coach — can book those empty future slots. Past-slot and other-member's-
  // booking access stays admin-only below (this widening is advance-window only).
  // A genuine block always arrives with a non-null booking, so the `!booking`
  // guard keeps real blocks greyed for all.
  const isUnavailable =
    slot.bookingStatus === 'Unavailable' && !(canBypassAdvanceWindow && !booking);
  const isOwnBooking = booking ? booking.membershipId === currentMembershipId : false;

  const isDisabled =
    (isUnavailable && !isAdmin) || (isPast && !isAdmin) || (!!booking && !isOwnBooking && !isAdmin);
  const canClick =
    (!isUnavailable || isAdmin) && (!isPast || isAdmin) && (!booking || isOwnBooking || isAdmin);

  return { isPast, isUnavailable, isOwnBooking, isDisabled, canClick };
}
